import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { HardHat, Briefcase, Sparkles, Eye, EyeOff } from "lucide-react";
import LocationAutocomplete from "../components/LocationAutocomplete";

export default function CompleteProfile() {
  const { user, refresh } = useAuth();
  const [role, setRole] = useState(user?.role || "worker");
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [homeLocation, setHomeLocation] = useState("");
  const [homeLat, setHomeLat] = useState(null);
  const [homeLng, setHomeLng] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const isWorker = role === "worker";

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(phone.trim())) { toast.error("Please enter a valid 10-digit phone number"); return; }
    if (!isWorker) {
      if (!username.trim()) { toast.error("Please choose a username"); return; }
      if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
      if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }
    }
    setSubmitting(true);
    try {
      await api.post("/auth/complete-profile", {
        name: name.trim() || undefined,
        username: isWorker ? undefined : username.trim(),
        password: isWorker ? undefined : password,
        role,
        phone: "+91" + phone.trim(),
        company_name: role === "manager" ? companyName.trim() : undefined,
      });
      if (isWorker && homeLat != null && homeLng != null) {
        // Optional — enables travel expense reimbursement later. Non-blocking on failure.
        try {
          await api.patch("/users/me", { location: homeLocation, lat: homeLat, lng: homeLng });
        } catch {
          // Worker can set this later from their profile instead.
        }
      }
      await refresh();
      toast.success("You're all set!");
      nav(redirectTo);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-12">
      <span className="label-eyebrow text-muted-foreground">Almost there</span>
      <h1 className="mt-2 font-display text-3xl tracking-tight">
        {isWorker ? "Just a couple details" : "Complete your profile"}
      </h1>
      <p className="mt-2 text-sm text-slate-600">
        {isWorker ? "We just need your name and phone number to get you applying." : "Pick a username and password for your team logins, then choose your role."}
      </p>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="complete-profile-form">
        <div>
          <label className="label-eyebrow text-muted-foreground">Full name</label>
          <input value={name} onChange={e => setName(e.target.value)} required className="input-base mt-2" data-testid="cp-name" />
        </div>
        <div>
          <label className="label-eyebrow text-muted-foreground">Phone number</label>
          <div className="flex items-center mt-2 input-base !p-0 overflow-hidden">
            <span className="px-3 py-2 text-sm text-muted-foreground bg-slate-50 border-r border-slate-200">+91</span>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} required maxLength={10} className="flex-1 px-3 py-2 outline-none bg-transparent" placeholder="XXXXX XXXXX" data-testid="cp-phone" />
          </div>
        </div>

        {isWorker && (
          <div>
            <label className="label-eyebrow text-muted-foreground">Home location <span className="text-xs normal-case text-muted-foreground">(optional)</span></label>
            <div className="mt-2">
              <LocationAutocomplete
                value={homeLocation}
                onChange={(val) => { setHomeLocation(val); setHomeLat(null); setHomeLng(null); }}
                onSelect={({ display, lat, lng }) => { setHomeLocation(display); setHomeLat(lat); setHomeLng(lng); }}
                placeholder="Search your home area…"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Lets us calculate travel allowance on jobs that offer it.</p>
            {homeLat != null && <p className="text-xs text-emerald-600 mt-1">✓ Location confirmed</p>}
          </div>
        )}

        {isWorker && (
          <button type="button" onClick={() => setRole("manager")} className="text-xs text-brand underline" data-testid="cp-switch-role">
            Setting up as a manager or freelancer instead?
          </button>
        )}

        {!isWorker && (
          <>
            <div>
              <label className="label-eyebrow text-muted-foreground">Role <span className="text-red-500">*</span></label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[["worker", HardHat], ["manager", Briefcase], ["freelancer", Sparkles]].map(([r, I]) => (
                  <button type="button" key={r} onClick={() => setRole(r)} data-testid={`cp-role-${r}`} className={`p-4 rounded-sm border-2 transition ${role===r?"border-brand bg-brand-tint":"border-slate-200 hover:border-brand"}`}>
                    <I className="w-5 h-5" />
                    <p className="mt-2 text-sm capitalize">{r}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-eyebrow text-muted-foreground">Username</label>
              <input value={username} onChange={e => setUsername(e.target.value.replace(/\s/g, "_").toLowerCase())} required minLength={3} className="input-base mt-2" data-testid="cp-username" />
            </div>
            <div>
              <label className="label-eyebrow text-muted-foreground">Create Password</label>
              <div className="relative mt-2">
                <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="input-base pr-10" data-testid="cp-password" />
                <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="label-eyebrow text-muted-foreground">Confirm password</label>
              <input type={showPassword ? "text" : "password"} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} className={`input-base ${confirmPassword && password !== confirmPassword ? "border-red-400" : ""}`} data-testid="cp-confirm-password" />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>
            {role === "manager" && (
              <div>
                <label className="label-eyebrow text-muted-foreground">Organization / company name</label>
                <input value={companyName} onChange={e => setCompanyName(e.target.value)} className="input-base mt-2" placeholder="e.g. Malabar Events Co." data-testid="cp-company-name" />
              </div>
            )}
          </>
        )}

        <button className="btn-primary w-full" disabled={submitting} data-testid="cp-submit">
          {submitting ? "Saving…" : "Continue"}
        </button>
      </form>
    </div>
  );
}
